import React, { useMemo, useRef, useState } from "react";

import { faChevronDown, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { faPlay } from "@fortawesome/pro-solid-svg-icons";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { useKeyDown } from "@react-hooks-library/core";
import { AnimatePresence, m } from "framer-motion";
import { observer } from "mobx-react-lite";

import { loadVersionOverridingIfNoneMatch } from "src/api";
import {
  CreateMultiversionTestRunVersion,
  MultiversionTestRunResults,
  RouterMultiversionTestRun,
  TestRunVersionProgress,
} from "src/api/endpoints";
import { Flow, FlowVersion } from "src/api/flowTypes";
import { useMultiversionTestRun } from "src/api/queries";
import {
  Dataset,
  ErrorBaseInfo,
  FlowRunnerResultV2,
  GraphRunResultType,
  JobRunnerResult,
} from "src/api/types";
import { NodeTypes } from "src/clients/flow-api";
import { NODE_TYPE } from "src/constants/NodeTypes";
import { useCreateDuplicateDatasetJob } from "src/datasets/api/queries";
import { useSelectedDatasetId } from "src/datasets/store/useSelectedDatasetId";
import { Icon } from "src/design-system/Icon";
import { useModal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { ProgressDropdown } from "src/flowContainer/RunFlowButton/ProgressDropdown";
import { RunBlockedModal } from "src/flowContainer/RunFlowButton/RunBlockedModal";
import { RunTestDropdown } from "src/flowContainer/RunFlowButton/RunTestDropdown";
import { useChildHasAgentNode } from "src/flowContainer/RunFlowButton/useChildHasAgentNodes";
import { useLoadTestRunListener } from "src/flowContainer/RunFlowButton/useLoadTestRunListener";
import { useTestRunOnJobs } from "src/flowContainer/RunFlowButton/useTestRunOnJobs";
import { hasAgentNodeWithLiveData } from "src/flowContainer/RunFlowButton/utils";
import { useStopTestRunListener } from "src/flowContainer/TestRunsSidePane/hooks";
import { useJobRunStatusCountsPolling } from "src/flowGraph/useJobRunCounts";
import { useIsIcebergEnabled } from "src/hooks/useIsIcebergEnabled";
import {
  tracker,
  trackingEvents,
} from "src/instrumentation/customTrackingEvents";
import { useTestRunJob } from "src/jobs/api/queries";
import { JobRun } from "src/jobs/types";
import {
  fetchSingleErrorPage,
  invalidateCompletionData,
} from "src/nodeEditor/api/queries";
import { MAX_LTR_ROW_COUNT } from "src/nodeEditor/JobRunIntermediateResultTableWrapper/hooks";
import { useAuthoringContext } from "src/router/routerContextHooks";
import {
  LeftPaneOptions,
  useSetLeftSidePaneOption,
  useToggleLeftSidePaneOption,
} from "src/router/SearchParams";
import { loginWithRefreshToken } from "src/store/api";
import { useNodeHighlighting } from "src/store/NodeHighlighting";
import { updateCurrentJobRun } from "src/store/runState/RunState";
import { useGraphStore } from "src/store/StoreProvider";
import { logger } from "src/utils/logger";
import { errorMessage } from "src/utils/stringUtils";

type Props = {
  flow: Flow;
  version: FlowVersion;
  datasets: Dataset[];
  isLoading: boolean;
};

const onRunError = async (err: unknown) => {
  logger.error(err);
  toastActions.failure({
    title: "Test run failed",
    description: errorMessage(err),
    actionText: "Send error report to Taktile",
    onActionClick: () => {
      window.open(
        "mailto:support@taktile.com",
        "_blank",
        "noopener noreferrer",
      );
    },
  });
};
const fillEtagFallbackIfNeeded = (
  versions: FlowVersion[],
): CreateMultiversionTestRunVersion[] =>
  versions.map((version) => ({
    id: version.id,
    etag: version.etag ?? "0000000",
    children: version.child_versions?.length
      ? version.child_versions?.map((child) => ({
          id: child.id,
          etag: child.etag ?? "0000000",
        }))
      : undefined,
  }));

// Wrapper for the additional version dropdowns
// since we need a way to preserve identity of the dropdowns
// for the animations to work
export type AdditionalVersionSelector = {
  selectorId: string;
  version: FlowVersion;
};

const getProgressPercentage = (progress: TestRunVersionProgress[]) => {
  const totalRowsToRun = progress.reduce((acc, curr) => acc + curr.total, 0);

  const processedRows = progress.reduce(
    (acc, curr) =>
      acc + curr.failed + curr.ignored + curr.successful + curr.output_mismatch,
    0,
  );

  return Math.floor((processedRows / totalRowsToRun) * 100);
};

const PLACEHOLDER_PROGRESS = {
  total: 100,
  successful: 0,
  failed: 0,
  ignored: 0,
  output_mismatch: 0,
};

const executionResultsToProgress = (
  result: FlowRunnerResultV2,
): TestRunVersionProgress => {
  if (result.type === GraphRunResultType.ERROR) {
    return PLACEHOLDER_PROGRESS;
  }
  return {
    total: result.nodes.output.total_count,
    successful: result.nodes.output.success_count,
    failed: result.nodes.output.failure_count,
    ignored: result.nodes.output.ignored_count,
    output_mismatch: result.nodes.output.expected_output_mismatch_count ?? 0,
  };
};

const MiniProgressBar: React.FC<{ progress: number }> = ({ progress }) => {
  return (
    <m.div
      key="mini-progress-bar"
      animate={{ width: 44 }}
      className="flex gap-x-0.5"
      // Start and end with the same
      // width as the play button
      exit={{ width: 18 }}
      initial={{ width: 18 }}
      role="progressbar"
    >
      {progress > 0 && (
        <m.div
          animate={{ width: `${progress}%` }}
          className="h-[3px] rounded-[100px] bg-white"
          initial={false}
          role="meter"
        ></m.div>
      )}
      <m.div
        animate={{ width: `${100 - progress}%` }}
        className="h-[3px] rounded-[100px] bg-white opacity-20"
        initial={false}
        role="meter"
      ></m.div>
    </m.div>
  );
};

enum TestRunStateType {
  STANDBY = "STANDBY",
  INITIATING = "INITIATING",
  IN_PROGRESS = "IN_PROGRESS",
}

type TestRunState =
  | {
      type: TestRunStateType.STANDBY;
    }
  | {
      type: TestRunStateType.INITIATING;
      versions: string[];
      abortController: AbortController;
      isJobRun: boolean;
    }
  | {
      type: TestRunStateType.IN_PROGRESS;
      versions: { name: string; progress: TestRunVersionProgress }[];
      abortController: AbortController;
      isJobRun: boolean;
    };

export const RunFlowButton: React.FC<Props> = observer(
  ({ flow, version: versionInFocus, datasets, isLoading }) => {
    const { workspace } = useAuthoringContext();

    const { data: testRunJob } = useTestRunJob({
      baseURL: workspace.base_url!,
      flowId: flow.id,
      flowVersionId: versionInFocus.id,
    });

    const isLongTestRunsEnabled = Boolean(
      versionInFocus.meta.enable_long_test_runs,
    );

    const {
      selectedNode,
      nodes,
      nodesArray,
      outputNode,
      selectErroredNode,
      selectOutputNode,
      setLastRunDatasetId,
      setDisplayedErroredRow,
      updateResults,
    } = useGraphStore();
    const { clearHighlighting } = useNodeHighlighting();
    const { selectedDatasetId, setSelectedDatasetId } = useSelectedDatasetId();
    const selectedDataset = datasets.find((d) => d.id === selectedDatasetId);
    const hasChildAgentNode = useChildHasAgentNode();
    const hasAgentNodes = useMemo(
      () => hasChildAgentNode || hasAgentNodeWithLiveData(nodesArray),
      [nodesArray, hasChildAgentNode],
    );

    const isIcebergEnabled = useIsIcebergEnabled();

    const {
      startTestRunOnJobs,
      stopTestRunOnJobs,
      pollJobRunInProgress,
      setCurrentJobRun,
      currentJobRun,
    } = useTestRunOnJobs();

    useJobRunStatusCountsPolling();

    const {
      isOpen: isRunBlockedModalOpen,
      data: runBlockedModalData,
      openModal: openRunBlockedModal,
      closeModal: closeRunBlockedModal,
    } = useModal<{
      flowVersionName: string;
      datasetName: string;
      datasetSize: number;
      aiNodeNames: string[];
      aiNodeTestRunRowLimit: number;
    }>();

    const { workspace: currentWs, orgId } = useAuthoringContext();
    const aiNodeTestRunRowLimit = Number(
      currentWs.config?.test_run_row_limit ?? 5,
    );

    const { mutateAsync: duplicateDataset, isPending: isDuplicatingDataset } =
      useCreateDuplicateDatasetJob(currentWs.base_url!);

    const toggleSidePaneOptions = useToggleLeftSidePaneOption();
    const setSidePaneOptions = useSetLeftSidePaneOption();
    const toggleDatasetsPane = () =>
      toggleSidePaneOptions(LeftPaneOptions.Datasets);

    const [additionalVersions, setAdditionalVersions] = useState<
      AdditionalVersionSelector[]
    >([]);
    const [testRunState, setTestRunState] = useState<TestRunState>({
      type: TestRunStateType.STANDBY,
    });

    const testRunMutation = useMultiversionTestRun(
      currentWs.base_url!,
      flow.id,
      flow.slug,
    );

    const onDuplicateDataset = async () => {
      if (!selectedDatasetId || !selectedDataset) return;

      try {
        await duplicateDataset({
          flow_id: flow.id,
          request: {
            source_dataset_id: selectedDatasetId,
            new_dataset_name: `${selectedDataset.name} (${aiNodeTestRunRowLimit} rows)`,
            row_limit: aiNodeTestRunRowLimit,
          },
        });
      } catch (err) {
        logger.error("Failed to duplicate dataset", err);
        toastActions.failure({
          title: `Something went wrong when duplicating ${selectedDataset.name}`,
        });
      }

      closeRunBlockedModal();
      toggleDatasetsPane();
    };

    const showNotificationAndTrack = async (
      data: FlowRunnerResultV2 | JobRunnerResult,
      isHistorical: boolean = false,
    ) => {
      if (data.type === GraphRunResultType.SUCCESS) {
        if (
          "nodes" in data &&
          data.nodes.output?.errors_pages_urls?.length > 0 &&
          currentWs.base_url
        ) {
          fetchSingleErrorPage({
            workspaceUrl: currentWs.base_url,
            result: data.nodes.output,
          }).then((errors) => {
            errors.forEach((error) => {
              tracker.emit(
                trackingEvents.authoringTestExecError({
                  flow_id: flow.id,
                  flow_version_id: versionInFocus.id,
                  run_id: testRunMutation.data?.id,
                  error_message: errorMessage(error.example_failure.msg),
                  exception_type: error.example_failure.exc_type,
                  status_code: error.example_failure.status_code,
                  organization_id: orgId,
                  node_id: error.example_failure.node_id,
                  node_type: error.example_failure.node_id
                    ? nodes.get(error.example_failure.node_id)?.type
                    : undefined,
                  type: "row_level",
                  failure_count: error.failure_count,
                }),
              );
            });
          });
        }
        toastActions.success({
          title: isHistorical ? "Test run loaded" : "Test run completed",
        });

        if (isHistorical && "jobRun" in data) {
          toastActions.dismiss(`test-run-loading-${data.jobRun.id}`);
        }
      } else if (data.type === GraphRunResultType.ERROR) {
        const trackError = (error: ErrorBaseInfo) => {
          tracker.emit(
            trackingEvents.authoringTestExecError({
              flow_id: flow.id,
              flow_version_id: versionInFocus.id,
              run_id: testRunMutation.data?.id,
              error_message: errorMessage(error.msg),
              exception_type: error.exc_type,
              status_code: error.status_code,
              organization_id: orgId,
              type: "flow_level",
              node_id: error.node_id,
              node_type: error.node_id
                ? nodes.get(error.node_id)?.type
                : undefined,
              failure_count: undefined,
            }),
          );
        };
        const numberOfNodesWithCompileError = new Set(
          data.errors?.filter((e) => e.node_id).map((e) => e.node_id),
        ).size;
        if (numberOfNodesWithCompileError <= 1) {
          const error = data.error;
          trackError(error);
          if (error.node_id) {
            const nodeName =
              nodes.get(error.node_id)?.type === NODE_TYPE.INPUT_NODE
                ? "input"
                : "output";

            toastActions.failure({
              title: "Test run failed",
              description: (
                <>
                  Check node{" "}
                  <span className="font-inter-semibold-13px text-gray-800">
                    {nodeName}
                  </span>{" "}
                  for details. Autocomplete suggestions will not be updated.
                </>
              ),
            });
          } else {
            toastActions.failure({
              title: "Test run failed",
              description: `Error: ${data.error.msg}`,
            });
          }
        } else if (data.errors) {
          data.errors.forEach(trackError);

          toastActions.failure({
            title: "Test run failed",
            description: `${numberOfNodesWithCompileError} nodes failed to compile. Autocomplete suggestions will not be updated.`,
          });
        }
      }
    };

    const onTestRunFinished = (
      data: MultiversionTestRunResults,
      isHistorical: boolean = false,
    ) => {
      const versionInFocusResults = data.flow_versions.find(
        (version) => version.id === versionInFocus.id,
      );
      const result = versionInFocusResults?.jobRun
        ? ({
            type: GraphRunResultType.SUCCESS,
            jobRun: versionInFocusResults.jobRun,
            datasetId: data.dataset.id,
          } as const)
        : versionInFocusResults?.execution_results;

      if (result) {
        showNotificationAndTrack(result, isHistorical);
        updateResults(nodesArray, result);
      }

      invalidateCompletionData(versionInFocus.id);
      setLastRunDatasetId(selectedDatasetId ?? null);
      setDisplayedErroredRow(undefined);
      const erroredNode = selectErroredNode();
      if (!erroredNode && !selectedNode) {
        selectOutputNode();
      }
    };

    const filterLiveAiNodes = (version: FlowVersion) => {
      return (
        version.graph?.nodes
          .filter((node) => node?.type === NodeTypes.AI_NODE_V2)
          .filter(
            (node) =>
              node?.meta?.config?.environments_config
                ?.authoring_mode_data_source !== "mock_data",
          ) ?? []
      );
    };

    const handleTestRun = async () => {
      // This abort controller is used in case
      // the test run is stopped, we check in multiple
      // points whether its been aborted
      const abortController = new AbortController();
      abortController.signal.addEventListener("abort", () => {
        setTestRunState({ type: TestRunStateType.STANDBY });
        toastActions.default({ title: "Test run stopped" });
      });

      const testRunVersions = [
        versionInFocus,
        ...additionalVersions.map((v) => v.version),
      ];
      setTestRunState({
        type: TestRunStateType.INITIATING,
        versions: testRunVersions.map((v) => v.name),
        abortController,
        isJobRun: false,
      });
      try {
        if (!selectedDataset) throw Error("no selectedDataset for run");
        clearHighlighting();

        // Make sure to get the latest etags
        const testRunVersionIds = testRunVersions.map((v) => v.id);
        const versions = await Promise.all(
          testRunVersionIds.map(loadVersionOverridingIfNoneMatch),
        );

        // For datasets with >10 rows we block the test run if
        // there is a live AI Node in any of the selected versions
        if (
          selectedDataset.row_count &&
          selectedDataset.row_count > aiNodeTestRunRowLimit
        ) {
          for (const version of versions) {
            const liveAiNodes = filterLiveAiNodes(version);

            if (liveAiNodes.length > 0) {
              // Abort the test run and open the modal
              setTestRunState({ type: TestRunStateType.STANDBY });
              openRunBlockedModal({
                flowVersionName: version.name,
                aiNodeNames: liveAiNodes.map((node) => node.name!),
                datasetName: selectedDataset.name,
                datasetSize: selectedDataset.row_count!,
                aiNodeTestRunRowLimit: aiNodeTestRunRowLimit,
              });
              return;
            }
          }
        }

        await loginWithRefreshToken();

        if (abortController.signal.aborted) {
          return;
        }

        const mutationResult = await testRunMutation.mutateAsync({
          dataset_id: selectedDataset.id,
          flow_versions: fillEtagFallbackIfNeeded(versions),
        });
        tracker.emit(
          trackingEvents.clickTestRun({
            organization_id: orgId,
            flow_id: flow.id,
            flow_version_id: versionInFocus.id,
            sample_data_id: selectedDataset.id,
            run_id: mutationResult.id,
          }),
        );
        const isVersionInFocusFinished = (
          response: MultiversionTestRunResults,
        ) => {
          const versionInFocusResult = response.flow_versions.find(
            (version) => version.id === versionInFocus.id,
          );

          return !!versionInFocusResult?.execution_results;
        };

        if (abortController.signal.aborted) {
          return;
        }

        if (isVersionInFocusFinished(mutationResult)) {
          onTestRunFinished(mutationResult);
          return;
        }

        let result = await RouterMultiversionTestRun.get(
          currentWs.base_url!,
          mutationResult.id,
        );

        if (abortController.signal.aborted) {
          return;
        }
        while (
          result.status === 200 &&
          !isVersionInFocusFinished(result.data)
        ) {
          await new Promise((r) => setTimeout(r, 1000));
          result = await RouterMultiversionTestRun.get(
            currentWs.base_url!,
            mutationResult.id,
          );
          const theresProgressAvailable = result.data.flow_versions.some(
            (version) => version.progress,
          );
          if (abortController.signal.aborted) {
            return;
          }
          if (theresProgressAvailable) {
            setTestRunState({
              isJobRun: false,
              type: TestRunStateType.IN_PROGRESS,
              versions: result.data.flow_versions.map((version) => ({
                name:
                  testRunVersions.find((v) => version.id === v.id)?.name ?? "",
                // If the progress is available we use it, otherwise we check
                // if the version has finished already (execution results would be populated)
                // if not we use a placeholder progress where everything is 0
                progress:
                  version.progress ??
                  (version.execution_results
                    ? executionResultsToProgress(version.execution_results)
                    : PLACEHOLDER_PROGRESS),
              })),
              abortController,
            });
          }
        }

        if (result.status !== 200) {
          throw new Error(
            `Error fetching results url, status: ${result.status} ${result.statusText}`,
          );
        }

        if (!abortController.signal.aborted) {
          onTestRunFinished(result.data);
        }
      } catch (e) {
        if (!abortController.signal.aborted) {
          onRunError(e);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setTestRunState({ type: TestRunStateType.STANDBY });
        }
      }
    };

    const getJobRunAbortController = () => {
      const abortController = new AbortController();
      abortController.signal.addEventListener("abort", async (event) => {
        const target = event.target as AbortSignal;

        // if stop test run event is from sidepane, we don't want to stop the job run,
        // as it will be handled by the sidepane
        if (target && target.reason === "sidepane") {
          setTestRunState({ type: TestRunStateType.STANDBY });
        } else {
          await stopTestRunOnJobs();
          setTestRunState({ type: TestRunStateType.STANDBY });
          toastActions.default({ title: "Test run stopped" });
        }
      });

      return abortController;
    };

    // handle job run
    const handleJobRun = async () => {
      if (!selectedDatasetId) {
        return;
      }

      const abortController = getJobRunAbortController();

      try {
        clearHighlighting();
        setDropdownState("closed");

        setTestRunState({
          type: TestRunStateType.INITIATING,
          versions: [versionInFocus.name],
          abortController,
          isJobRun: true,
        });

        const jobRun = await startTestRunOnJobs({
          datasetId: selectedDatasetId,
          datasetName: selectedDataset?.name ?? "",
        });

        if (abortController.signal.aborted) {
          setTestRunState({ type: TestRunStateType.STANDBY });
          return;
        }

        loadJobRun({
          jobRun,
          abortController,
          datasetId: selectedDatasetId,
          datasetEtag: selectedDataset?.etag ?? "",
        });
      } catch (error) {
        if (!abortController.signal.aborted) {
          onRunError(error);
          setTestRunState({ type: TestRunStateType.STANDBY });
          if (currentJobRun.current?.runId) {
            toastActions.dismiss(
              `test-run-loading-${currentJobRun.current.runId}`,
            );
          }
        }
      }
    };

    const loadJobRun = async ({
      jobRun,
      abortController: abortControllerArg,
      datasetId,
      datasetEtag,
      isHistorical = false,
    }: {
      jobRun: JobRun;
      abortController?: AbortController;
      datasetId: string;
      datasetEtag: string;
      isHistorical?: boolean;
    }) => {
      const abortController = abortControllerArg ?? getJobRunAbortController();
      const datasetRowCount =
        datasets.find((d) => d.id === datasetId)?.row_count ?? 1;

      try {
        clearHighlighting();
        setCurrentJobRun(jobRun);
        setLastRunDatasetId(datasetId);
        updateCurrentJobRun(jobRun);

        // Set run state immediately so the results table renders during polling
        updateResults(nodesArray, {
          type: GraphRunResultType.JOB_IN_PROGRESS,
          jobRun,
          datasetId,
        });

        if (!isHistorical) {
          setTestRunState({
            isJobRun: true,
            type: TestRunStateType.IN_PROGRESS,
            versions: [
              { name: versionInFocus.name, progress: PLACEHOLDER_PROGRESS },
            ],
            abortController,
          });
        }

        const result = await pollJobRunInProgress({
          expectedTotal: datasetRowCount,
          outputNodeId: outputNode?.id ?? "",
          jobRun,
          callback: (progress) => {
            if (abortController.signal.aborted) {
              return;
            }
            updateResults(nodesArray, {
              type: GraphRunResultType.JOB_IN_PROGRESS,
              jobRun: {
                ...jobRun,
                updated_at: new Date().toISOString(),
                success_rows_count: progress.successful,
                failed_rows_count: progress.failed,
                ignored_rows_count: progress.ignored,
                total_rows_count: progress.total,
              },
              datasetId,
            });
            if (!isHistorical) {
              setTestRunState({
                type: TestRunStateType.IN_PROGRESS,
                versions: [{ name: versionInFocus.name, progress }],
                abortController,
                isJobRun: true,
              });
            }
          },
        });

        if (!abortController.signal.aborted && result?.counts) {
          const progress = {
            total: datasetRowCount,
            successful: result.counts.totalSuccess,
            failed: result.counts.totalFailed,
            ignored: result.counts.totalIgnored,
            output_mismatch: result.counts.totalOutputMismatch,
          } as const;

          onTestRunFinished(
            {
              id: jobRun.id,
              infer_types: false,
              created_by: "",
              created_at: jobRun.created_at,
              dataset: {
                id: datasetId,
                etag: datasetEtag,
                shard_count: 0,
              },
              flow_versions: [
                {
                  id: versionInFocus.id,
                  jobRun: result.jobRun,
                  execution_results: null,
                  etag: versionInFocus.etag ?? "",
                  progress,
                },
              ],
            },
            isHistorical,
          );

          setDropdownState("closed");
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          onRunError(error);
          if (currentJobRun.current?.runId) {
            toastActions.dismiss(
              `test-run-loading-${currentJobRun.current.runId}`,
            );
          }
        }
      } finally {
        if (!abortController.signal.aborted) {
          setTestRunState({ type: TestRunStateType.STANDBY });
        }
      }
    };

    /**
     * CMD/CTRL + Enter should be equivalent as clicking the button
     * if the shortcut is enabled. We disable it if a modal (which would intercept a click too)
     * is open.
     */

    const buttonRef = useRef<HTMLDivElement>(null);

    useKeyDown(["Enter"], (e) => {
      const modalIsOpen = document.querySelector('[data-loc="modal"]');
      if ((e.metaKey || e.ctrlKey) && !modalIsOpen) {
        e.preventDefault();
        buttonRef.current?.click();
      }
    });

    useStopTestRunListener((event) => {
      if (
        event.detail.source === "sidepane" &&
        testRunState.type !== "STANDBY"
      ) {
        testRunState.abortController.abort(event.detail.source);
      }
    });

    useLoadTestRunListener(({ jobRun, datasetId, datasetEtag }) => {
      if (testRunState.type !== "STANDBY") return;

      if (
        currentJobRun.current?.jobId === jobRun.job_id &&
        currentJobRun.current?.runId === jobRun.id
      ) {
        return;
      }

      toastActions.loading({
        title: "Loading test run",
        id: `test-run-loading-${jobRun.id}`,
      });
      loadJobRun({
        jobRun,
        datasetId,
        datasetEtag,
        isHistorical: true,
      });
    });

    const [dropdownState, setDropdownState] = useState<
      "opened_by_click" | "opened_by_hover" | "closed"
    >("closed");

    return (
      <>
        <Popover
          as="div"
          className="relative"
          onMouseEnter={() => {
            // disable dropdown if job run is in progress
            if (testRunState.type !== "STANDBY" && testRunState.isJobRun) {
              return;
            }

            if (dropdownState === "closed" && testRunState.type !== "STANDBY")
              setDropdownState("opened_by_hover");
          }}
          onMouseLeave={() => {
            if (dropdownState === "opened_by_hover") setDropdownState("closed");
          }}
        >
          <PopoverButton
            as="div"
            className="relative flex cursor-pointer items-center rounded-lg bg-indigo-600 font-inter-normal-13px text-white"
            onClick={() => {
              // disable dropdown if job run is in progress
              if (testRunState.type !== "STANDBY" && testRunState.isJobRun) {
                return;
              }

              if (dropdownState === "closed") {
                setDropdownState("opened_by_click");
              } else {
                setDropdownState("closed");
              }
            }}
          >
            {/* This invisible element prevents the popover from being hidden when the mouse goes from the button to the popover panel */}
            <div className="absolute right-0 -bottom-3 left-0 h-3"></div>
            <m.div
              ref={buttonRef}
              animate={{
                width: testRunState.type === "STANDBY" ? "69px" : "126px",
              }}
              className="flex items-center py-1.5 pr-2 pl-3"
              data-loc="run-flow-button"
              onClick={async (e) => {
                const rowCount = selectedDataset?.row_count ?? 0;
                const canTestRun =
                  selectedDatasetId &&
                  !isLoading &&
                  testRunState.type === "STANDBY";

                if (!canTestRun) return;
                e.stopPropagation();

                if (
                  isIcebergEnabled &&
                  (isLongTestRunsEnabled || hasAgentNodes)
                ) {
                  if (rowCount > MAX_LTR_ROW_COUNT) {
                    toastActions.failure({
                      title: `Agent test runs are not supported for datasets with more than ${MAX_LTR_ROW_COUNT} rows`,
                    });
                  } else {
                    handleJobRun();
                  }
                } else {
                  handleTestRun();
                }
              }}
            >
              <div className="relative mr-1 flex items-center">
                <AnimatePresence initial={false} mode="wait">
                  {testRunState.type === "STANDBY" ? (
                    <m.div
                      key="play-icon"
                      animate={{
                        opacity: 1,
                      }}
                      initial={{ opacity: 0 }}
                    >
                      <Icon variant="inverted" icon={faPlay} size="2xs" />
                    </m.div>
                  ) : (
                    <MiniProgressBar
                      key="progress-bar"
                      progress={
                        testRunState.type === "IN_PROGRESS"
                          ? getProgressPercentage(
                              testRunState.versions.map((v) => v.progress),
                            )
                          : 0
                      }
                    />
                  )}
                </AnimatePresence>
              </div>
              <span className="overflow-hidden font-inter-medium-13px">
                {testRunState.type === "STANDBY" ? "Test" : "Testing..."}
              </span>
            </m.div>
            <div
              className="py-1.5 pr-3"
              data-loc="run-flow-button-dropdown"
              onClick={(e) => {
                if (testRunState.type !== "STANDBY") {
                  e.stopPropagation();
                  testRunState.abortController.abort();
                }
              }}
            >
              <div className="flex items-center border-indigo-500 border-l py-0.5 pl-1.5">
                <div className="block">
                  <Icon
                    variant="inverted"
                    icon={
                      testRunState.type === "STANDBY" ? faChevronDown : faTimes
                    }
                    size="2xs"
                  />
                </div>
              </div>
            </div>
          </PopoverButton>
          <AnimatePresence>
            {dropdownState !== "closed" && (
              <PopoverPanel
                anchor={{
                  to: "bottom end",
                  gap: 6,
                }}
                animate="visible"
                as={m.div}
                className="rounded-lg bg-white text-gray-800 shadow-2xl outline-none"
                data-loc="run-flow-button-dropdown-panel"
                exit="hidden"
                initial="hidden"
                variants={{
                  visible: {
                    opacity: 1,
                    scale: 1,
                    transition: {
                      type: "tween",
                      ease: "easeOut",
                      duration: 0.15,
                    },
                  },
                  hidden: {
                    opacity: 0,
                    scale: 0.95,
                    transition: {
                      type: "tween",
                      ease: "easeOut",
                      duration: 0.15,
                    },
                  },
                }}
                static
              >
                {testRunState.type === "STANDBY" ? (
                  testRunJob && (
                    <RunTestDropdown
                      key={versionInFocus.id}
                      additionalVersions={additionalVersions}
                      datasets={datasets}
                      flow={flow}
                      hasAgentNodes={hasAgentNodes}
                      isLoading={isLoading}
                      selectedDatasetId={selectedDatasetId}
                      setAdditionalVersions={setAdditionalVersions}
                      testRunJob={testRunJob}
                      version={versionInFocus}
                      onClose={() => setDropdownState("closed")}
                      onGoToSimulations={() =>
                        setSidePaneOptions(LeftPaneOptions.Simulations)
                      }
                      onSelectData={setSelectedDatasetId}
                    />
                  )
                ) : (
                  <ProgressDropdown
                    showOutputMismatch={true}
                    versions={
                      testRunState.type === "IN_PROGRESS"
                        ? testRunState.versions
                        : testRunState.versions.map((versionName) => ({
                            name: versionName,
                            progress: PLACEHOLDER_PROGRESS,
                          }))
                    }
                  />
                )}
              </PopoverPanel>
            )}
          </AnimatePresence>
        </Popover>
        <RunBlockedModal
          flow={flow}
          isLoading={isDuplicatingDataset}
          isOpen={isRunBlockedModalOpen}
          onClose={closeRunBlockedModal}
          onDuplicateDataset={onDuplicateDataset}
          {...runBlockedModalData}
        />
      </>
    );
  },
);
